import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { NbDialogRef, NbSpinnerModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { AISearchFormSchema, AISearchResponse, DatabaseService } from '../../services/database.service'

/**
 * AI Search Modal Component
 * Allows users to search the database using natural language queries
 */
@Component({
  selector: 'app-ai-search-modal',
  templateUrl: './ai-search-modal.component.html',
  styleUrls: ['./ai-search-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, NbSpinnerModule]
})
export class AiSearchModalComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>()

  /** The current form/persona to search within */
  @Input() form: Persona

  /** Event days for system field context */
  @Input() eventDays: string[] = []

  /** Emits when filters are generated successfully */
  @Output() filtersGenerated = new EventEmitter<AISearchResponse['filters']>()

  /** The user's search query */
  query = ''

  /** Loading state */
  isLoading = false

  /** Error message if any */
  errorMessage: string | null = null

  /** Example queries to help users */
  exampleQueries = [
    'Show me all approved registrations from UAE',
    'People who registered in the last week and are not yet approved',
    'Registrations with bounced emails',
    'All attendees from Qatar who missed day 1',
    'Pending registrations sorted by newest first',
    'People whose email has not been delivered'
  ]

  constructor(
    private dialogRef: NbDialogRef<AiSearchModalComponent>,
    private databaseService: DatabaseService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * Use an example query
   */
  useExample(example: string): void {
    this.query = example
    this.errorMessage = null
  }

  /**
   * Submit the search query to AI
   */
  submitSearch(): void {
    if (!this.query.trim()) {
      this.errorMessage = 'Please enter a search query'
      return
    }

    this.isLoading = true
    this.errorMessage = null

    const formSchema = this.buildFormSchema()

    this.databaseService
      .generateAIFilters(this.query, formSchema)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: AISearchResponse) => {
          this.isLoading = false
          if (response.success && response.filters) {
            this.filtersGenerated.emit(response.filters)
            this.dialogRef.close(response.filters)
          } else {
            this.errorMessage = response.error || 'Failed to generate filters. Please try a different query.'
          }
        },
        error: (err) => {
          this.isLoading = false
          console.error('[AI-SEARCH] Error:', err)
          if (err.status === 503) {
            this.errorMessage = 'AI search is not configured. Please contact your administrator.'
          } else if (err.status === 400) {
            this.errorMessage = err.error?.error || 'Invalid search query. Please try again.'
          } else {
            this.errorMessage = 'Something went wrong. Please try again later.'
          }
        }
      })
  }

  /**
   * Build the form schema for AI context
   */
  private buildFormSchema(): AISearchFormSchema {
    const fields: AISearchFormSchema['fields'] = []

    if (this.form?.form_elements) {
      this.form.form_elements.forEach((element: PersonaField) => {
        // Skip non-filterable field types
        if ([11, 12, 13].includes(element.form_element_type_id)) {
          // Information message, warning, line separator
          return
        }

        const field: AISearchFormSchema['fields'][0] = {
          name: element.name,
          label: element.label,
          type: this.getFieldTypeName(element.form_element_type_id)
        }

        // Add options for select/radio/checkbox fields
        if (element.form_element_values?.length > 0) {
          field.options = element.form_element_values.map((v) => ({
            slug: v.slug,
            name: v.name
          }))
        }

        fields.push(field)
      })
    }

    return {
      fields,
      systemFields: {
        days: this.eventDays
      }
    }
  }

  /**
   * Get human-readable field type name
   */
  private getFieldTypeName(typeId: number): string {
    const typeMap: { [key: number]: string } = {
      1: 'fullname',
      2: 'email',
      3: 'phone',
      4: 'country',
      5: 'organization',
      6: 'job_title',
      7: 'gender',
      8: 'radio',
      9: 'checkbox',
      10: 'date',
      14: 'numeric',
      15: 'plain_text',
      16: 'upload',
      17: 'url',
      18: 'age_group',
      19: 'city'
    }
    return typeMap[typeId] || 'text'
  }

  /**
   * Close the modal
   */
  close(): void {
    this.dialogRef.close()
  }

  /**
   * Handle Enter key in textarea
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.submitSearch()
    }
  }
}
